import React, { useRef, useState, useCallback, useEffect } from 'react';
import ExportControls from './ExportControls';
import {
  copyElementToClipboard,
  saveElementToDevice,
  generateOverlayFilename,
  type ExportStyle,
} from '@/utils/overlayExport';

const isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window;

interface OverlayExportWrapperProps {
  /** Human-readable name for the metric (used in filename) */
  metricName: string;
  /** Optional athlete name (used in filename) */
  athleteName?: string;
  children: React.ReactNode;
  /** Additional className on the outer wrapper */
  className?: string;
  /** Show the styled/transparent toggle button (default true) */
  showStyleToggle?: boolean;
  /** CSS class overrides for toolbar position (default "top-2 right-2") */
  toolbarClassName?: string;
  /** Called before capture starts — use to toggle export-only visuals */
  onBeforeCapture?: () => void | Promise<void>;
  /** Called after capture completes — use to revert export-only visuals */
  onAfterCapture?: () => void | Promise<void>;
}

const OverlayExportWrapper: React.FC<OverlayExportWrapperProps> = ({
  metricName,
  athleteName,
  children,
  className = '',
  showStyleToggle = false,
  toolbarClassName = 'top-2 right-2',
  onBeforeCapture,
  onAfterCapture,
}) => {
  const captureRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [exportStyle, setExportStyle] = useState<ExportStyle>('styled');
  const [isVisible, setIsVisible] = useState(false);

  // Mobile: tap wrapper to toggle toolbar, auto-hide after 4s
  const handleTap = useCallback(() => {
    if (!isTouchDevice) return;
    setIsVisible((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isTouchDevice || !isVisible) return;
    const timer = setTimeout(() => setIsVisible(false), 4000);
    return () => clearTimeout(timer);
  }, [isVisible]);

  // Mobile: dismiss on outside tap
  useEffect(() => {
    if (!isTouchDevice || !isVisible) return;
    const handler = (e: TouchEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsVisible(false);
      }
    };
    document.addEventListener('touchstart', handler);
    return () => document.removeEventListener('touchstart', handler);
  }, [isVisible]);

  const handleDownload = useCallback(async () => {
    if (!captureRef.current) {
      console.error('[OverlayExport] captureRef is null');
      return;
    }
    try {
      await onBeforeCapture?.();
      // Small delay to let React re-render (e.g. canvas redraw with overlay)
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const filename = generateOverlayFilename(metricName, athleteName);
      await saveElementToDevice(captureRef.current, filename, { style: exportStyle });
    } catch (err) {
      console.error('[OverlayExport] Download failed:', err);
    } finally {
      await onAfterCapture?.();
    }
  }, [exportStyle, metricName, athleteName, onBeforeCapture, onAfterCapture]);

  const handleCopy = useCallback(async () => {
    if (!captureRef.current) {
      console.error('[OverlayExport] captureRef is null');
      return;
    }
    try {
      await onBeforeCapture?.();
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const success = await copyElementToClipboard(captureRef.current, { style: exportStyle });
      if (!success) console.warn('[OverlayExport] Clipboard write returned false');
    } catch (err) {
      console.error('[OverlayExport] Copy failed:', err);
    } finally {
      await onAfterCapture?.();
    }
  }, [exportStyle, onBeforeCapture, onAfterCapture]);

  const handleStyleToggle = useCallback(() => {
    setExportStyle((prev) => (prev === 'styled' ? 'transparent' : 'styled'));
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`relative group ${className}`}
      onMouseEnter={isTouchDevice ? undefined : () => setIsVisible(true)}
      onMouseLeave={isTouchDevice ? undefined : () => setIsVisible(false)}
      onClick={isTouchDevice ? handleTap : undefined}
    >
      {/* Capture target — wraps the children exactly */}
      <div ref={captureRef}>
        {children}
      </div>

      {/* Floating export toolbar — hover on desktop, tap on mobile */}
      <div
        data-overlay-toolbar="true"
        className={`absolute ${toolbarClassName} z-10 transition-all duration-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="cgm-card px-2 py-1.5 shadow-lg backdrop-blur-sm" style={{ borderColor: 'var(--card-border)' }}>
          <ExportControls
            onDownload={handleDownload}
            onCopy={handleCopy}
            exportStyle={exportStyle}
            onStyleToggle={handleStyleToggle}
            showStyleToggle={showStyleToggle}
          />
        </div>
      </div>
    </div>
  );
};

export default OverlayExportWrapper;
